import { Box, Grid2 } from '@mui/material';
import CustomCard from '@ui-components/Card/CustomCard';
import CustomTextField from '@ui-components/TextField/CustomTextField';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import CustomLink from '@ui-components/Link/CustomLink';
import QLerLogo from '@assets/images/qler_Logo.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import CustomSnackbar from '@ui-components/Snackbar/CustomSnackbar';
import type { AlertColor } from '@mui/material';
import {
  useFacilityResetPassword,
  useCheckEmailExpire,
} from '../../API/hooks/loginHook';
import type {
  FacilityResponce,
  ResetPassword as ResetPasswordType,
} from '../../API/services/loginServices';
import type { ErrorType } from '../../shared/lib/utils/errorType';
import Footer from './Footer';
import { getUserFromStorage } from '../components/storageUtility';
import {
  FACILITY_LINK_EXPIRE_ROUTE_PATH,
  TWO_FACTOR_AUTHENTICATION_ROUTE_PATH,
} from '../../routers/RoutPath';

const initialValues: ResetPasswordType = {
  userId: '',
  newPassword: '',
  confirmPassword: '',
};
const validationData =
  'Password must be between 8 and 50 characters long and include at least one number, one lowercase letter, one uppercase letter, and one special character.';
const passwordValidationSchema = Yup.string()
  .min(8, validationData)
  .max(50, validationData)
  .matches(/\d/, validationData)
  .matches(/[a-z]/, validationData)
  .matches(/[A-Z]/, validationData)
  .matches(/[^a-zA-Z0-9]/, validationData)
  .required('New password is required');
const validationSchema = Yup.object({
  newPassword: passwordValidationSchema,
  confirmPassword: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match!'),
});
const ResetPassword = () => {
  const { uid } = useParams<{ uid?: string }>();
  const pendingUserData = sessionStorage.getItem('pendingMfaUser');
  const pendingUser = pendingUserData ? JSON.parse(pendingUserData) : null;
  const user = getUserFromStorage();
  const userId =
    uid ?? pendingUser?.userId?.toString() ?? user?.userId?.toString() ?? '';

  const [openToast, setOpenToast] = useState(false);
  const [messageType, setMessageType] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { mutate: mutateResetPassword } = useFacilityResetPassword();
  const { emailExpireData } = useCheckEmailExpire(uid ?? '');

  useEffect(() => {
    if (
      emailExpireData &&
      (emailExpireData.code === 404 || emailExpireData?.code === 400) &&
      !emailExpireData.valid
    ) {
      navigate(FACILITY_LINK_EXPIRE_ROUTE_PATH, { replace: true });
    }
  }, [emailExpireData, navigate]);

  const handleResetPassword = (request: ResetPasswordType) => {
    mutateResetPassword(
      {
        userId: userId,
        newPassword: request.newPassword,
        confirmPassword: request.confirmPassword,
      },
      {
        onSuccess: (data: FacilityResponce) => {
          if (data.code === 200) {
            setOpenToast(true);
            setMessageType('success');
            setError(data.message);
            setTimeout(() => {
              setOpenToast(false);

              if (pendingUser?.requiredMfa) {
                navigate(TWO_FACTOR_AUTHENTICATION_ROUTE_PATH, {
                  replace: true,
                });
              } else {
                sessionStorage.removeItem('pendingMfaUser');
                navigate('/', { replace: true });
              }
            }, 3000);
          }
        },

        onError: (error: Error | ErrorType) => {
          setOpenToast(true);
          setMessageType('error');
          setError(error.message);
          setTimeout(() => {
            setOpenToast(false);
          }, 3000);
        },
      }
    );
  };
  return (
    <>
      {openToast && (
        <CustomSnackbar
          id="demo-snackbar"
          successOrError={messageType as AlertColor}
          open={openToast}
          toastrMessage={error}
        />
      )}
      <Box className="container">
        <Box className="center-div">
          <Box className="logo-qler">
            <QLerLogo />
          </Box>
          <Box>
            <Formik
              initialValues={initialValues}
              validateOnMount={true}
              enableReinitialize={true}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                handleResetPassword(values);
              }}
            >
              {({ handleSubmit, setFieldValue, values }) => (
                <Grid2 container justifyContent="center">
                  <CustomCard
                    className="card-width login-form"
                    id="cardWithoutImage"
                  >
                    <CustomTypography
                      className="pb-12 mt-40"
                      id="resettext"
                      variant="h3"
                    >
                      Reset Password
                    </CustomTypography>
                    <CustomTypography
                      id="supportingText"
                      variant="body2"
                      color="textSecondary"
                      className="pb-32 "
                    >
                      Please enter a new password that is between 8 and 50
                      characters long and includes at least one number, one
                      lowercase letter, one uppercase letter, and one special
                      character.
                    </CustomTypography>

                    <Box
                      display="flex"
                      justifyContent="center"
                      className="pb-16"
                    >
                      <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                        <CustomTextField
                          id="newPassword"
                          variant="outlined"
                          placeholder="New Password"
                          value={values.newPassword}
                          onChange={(e) =>
                            setFieldValue('newPassword', e.target.value)
                          }
                          size="small"
                          type="password"
                          isPassword={true}
                        />
                        <CustomTypography
                          className="float-left text-align-left"
                          id="errorMessage-newpassword"
                          variant="body3"
                          color="error"
                        >
                          <ErrorMessage name="newPassword" component="div" />
                        </CustomTypography>
                      </Grid2>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="center"
                      className="pb-24"
                    >
                      <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                        <CustomTextField
                          id="confirmPasswordid"
                          variant="outlined"
                          placeholder="Confirm Password"
                          value={values.confirmPassword}
                          onChange={(e) =>
                            setFieldValue('confirmPassword', e.target.value)
                          }
                          size="small"
                          type="password"
                          isPassword={true}
                        />
                        <CustomTypography
                          className="float-left"
                          id="errorMessage-confirmpassword"
                          variant="body3"
                          color="error"
                        >
                          <ErrorMessage
                            name="confirmPassword"
                            component="div"
                          />
                        </CustomTypography>
                      </Grid2>
                    </Box>

                    <Box className="pb-24">
                      <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                        <CustomButton
                          id="btnLarge"
                          variant="contained"
                          size="large"
                          className="button-width button-height button-font-size pt-28"
                          onClick={() => handleSubmit()}
                        >
                          Submit
                        </CustomButton>
                      </Grid2>
                    </Box>

                    <Box className="pb-24">
                      <Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
                        <CustomLink
                          id="demo-link"
                          color="info"
                          variant="h6"
                          underline="none"
                          onClick={() => {
                            localStorage.setItem('screen', 'login');
                            navigate('/', { replace: true });
                          }}
                        >
                          Back to Login
                        </CustomLink>
                      </Grid2>
                    </Box>
                    <CustomTypography
                      id="demo-h2"
                      variant="body2"
                      color="textSecondary"
                    >
                      Having Difficulty? Please call
                    </CustomTypography>
                    <CustomTypography id="demo-h2" variant="h6">
                      (855) 797-7879
                    </CustomTypography>
                  </CustomCard>
                </Grid2>
              )}
            </Formik>
          </Box>
        </Box>
        <Footer />
      </Box>
    </>
  );
};

export default ResetPassword;
